import type { BillingFeature } from 'webapp/enums/enums';

import type { Source } from 'webapp/models-custom';
import { DataSourceSyncStatus, DataSourceSyncType } from 'webapp/models/source';

export const mapSourceToDataSource = (
	source: Source,
	currentUserId: number | null,
	spaceId?: number,
) => {
	return {
		avatar: source.realAvatar,
		connection: source.connection
			? {
					id: source.connection.id,
					name: source.connection.name,
					avatar: source.sourceType?.avatar,
					connectionNumberOfSources: source.connection.numberOfSources ?? 0,
					ownerId: source.connection.userId ?? null,
					sharableStatus: source.connection.sharableStatus ?? null,
					permissionStatus: source.connection.permissionStatus ?? null,
				}
			: undefined,
		created: source.created || null,
		createdBy: {
			userId: Number(source.createdBy?.id),
			authId: '',
			email: source.createdBy?.email ?? '',
			name: source.createdBy?.name ?? '',
			avatar: source.createdBy?.avatar ?? '',
			isOwner: source.ownerId === Number(source.createdBy?.id),
			count: 0,
		},
		id: source.numericId!,
		owner: {
			userId: source.ownerId,
			authId: '',
			email: '',
			name: '',
			avatar: '',
			count: 0,
			isOwner: source.ownerId === currentUserId,
		},
		statusObject: {
			title: source.statusTitle,
			description: source.statusDescription,
			trigger: '',
			level: source.statusObject?.level,
			id: -1,
			userAction: source.statusAction,
		},
		syncInterval: source.syncInterval,
		title: source.title,
		selectedSourceOption: source.selectedSourceOption,
		type: source.type,
		typeName: source.sourceType.name,
		sourceId: +source.id!,
		initialTitle: source.initialTitle,
		subtitle: source.subtitle,
		avatarOriginal: source.realAvatar,
		wrapperFor: source.wrapperFor ?? '',
		isRoot: source.isRoot,
		isLocked: source.isLocked,
		lastActivity: null,
		metadata: null,
		metadataStatus: 0,
		permissions: {
			permissionType: source.permissionsEveryone
				? 0 // EVERYONE
				: !source.haveBasicPermissions
					? 2 // SHARED_WITH_ME
					: 1, // ME_AND_X_OTHERS
			sharedWith: source.users.filter((id: number) => id !== currentUserId).map(String),
		},
		clientId: spaceId,
		token: source.token,
		isTypeIngest: source.isTypeIngest,
		timezone: source.timezone,
		verificationInfo: source.verificationInfo ?? null,
		typeDetails: {
			canReconnect: !!source.connection?.canReconnect,
			isFullyEditable: source.sourceType.isWithCustomToken && !source.wrapperFor,
			canEditTimezone: source.canEditTimezone,
			canEditConnection: false,
			id: source.sourceType.id ?? 0,
			key: source.sourceType.key,
			name: source.sourceType.name,
			avatar: source.sourceType.avatar,
			wrapperFor: '',
			availableSourceOptions: source.sourceType.availableSourceOptions ?? [],
			availableMetricOptions: source.sourceType.availableMetricOptions ?? [],
			horizonStatus: source.sourceType.horizonStatus ?? null,
		},
	};
};

export const mapSyncInfo = (lastSync: Source['lastSync']) => {
	if (!lastSync) {
		return undefined;
	}

	return {
		startedAt: lastSync.startedAt,
		finishedAt: lastSync?.finishedAt,
		syncType: mapSyncType(lastSync.syncType),
		syncStatus: mapSyncStatus(lastSync.syncStatus),
		lastSuccessfulSyncAt: lastSync?.lastSuccessfulSyncAt,
		dataRangeFrom: lastSync?.dataRangeFrom,
		dataRangeTo: lastSync?.dataRangeTo,
		dataAvailableAt: lastSync?.dataAvailableAt,
	};
};

const mapSyncType = (syncType: DataSourceSyncType): string => {
	switch (syncType) {
		case DataSourceSyncType.INITIAL_SYNC:
			return 'initialSync';
		case DataSourceSyncType.USER_REQUEST:
			return 'userRequest';
		case DataSourceSyncType.MODIFICATION_REQUEST:
			return 'modificationRequest';
		case DataSourceSyncType.SCHEDULED_UPDATE:
			return 'scheduledUpdate';
	}
};

const mapSyncStatus = (syncStatus: DataSourceSyncStatus): string => {
	switch (syncStatus) {
		case DataSourceSyncStatus.SUCCESS:
			return 'success';
		case DataSourceSyncStatus.FAILED:
			return 'failed';
		case DataSourceSyncStatus.IN_PROGRESS:
			return 'inProgress';
		case DataSourceSyncStatus.PARTIAL_SYNC:
			return 'partiallyDone';
		case DataSourceSyncStatus.SUCCESS_WITH_WARNINGS:
			return 'successWithWarnings';
		case DataSourceSyncStatus.CANCELED:
			return 'canceled';
	}
};

export const getBillingFeature = (featureName: string): BillingFeature =>
	featureName as BillingFeature;
